// jotai store: one atom per market (atomFamily) + the apply functions that
// translate parsed payloads into atom writes. Pure store logic — no React.

import { atom, getDefaultStore } from 'jotai';
import { atomFamily } from 'jotai/utils';

type Store = ReturnType<typeof getDefaultStore>;
import { appendHistory, parseEventWithMarkets, parseMarket } from './parse';
import type {
  EventGroup,
  EventSnapshot,
  MarketSnapshot,
  PricePoint,
  SettledEntry,
  UpcomingEntry,
} from './types';

export const eventsAtom = atom<Map<string, EventSnapshot>>(new Map());
export const marketIdsAtom = atom<number[]>([]);
export const settledAtom = atom<SettledEntry[]>([]);
export const archiveAtom = atom<SettledEntry[]>([]);
export const marketAtom = atomFamily((_: number) =>
  atom<MarketSnapshot | undefined>(undefined),
);

/** Bootstrap (GET /events) — replaces all state; also used by event:added */
export function applyEventWithMarkets(
  store: Store,
  rawEvent: Parameters<typeof parseEventWithMarkets>[0],
): void {
  const { event, markets } = parseEventWithMarkets(rawEvent);
  const events = new Map(store.get(eventsAtom));
  events.set(event.eventId, event);
  store.set(eventsAtom, events);
  const ids = new Set(store.get(marketIdsAtom));
  for (const market of markets) {
    // event:update/added deltas re-parse markets with empty history — keep
    // the rolling window (same contract as hydrateHistory: deltas never
    // clobber). Without this, every event:update wipes sparkline history;
    // static pre-match quotes never re-append and their sparklines go blank.
    const existing = store.get(marketAtom(market.index));
    store.set(
      marketAtom(market.index),
      existing ? { ...market, history: existing.history } : market,
    );
    ids.add(market.index);
  }
  store.set(marketIdsAtom, [...ids]);
}

/** Full re-seed from GET /events (initial load, reconnect, staleness) */
export function applyBootstrap(
  store: Store,
  rawEvents: Parameters<typeof parseEventWithMarkets>[0][],
): void {
  store.set(eventsAtom, new Map());
  store.set(marketIdsAtom, []);
  for (const raw of rawEvents) {
    applyEventWithMarkets(store, raw);
  }
}

export function setSettled(store: Store, entries: SettledEntry[]): void {
  store.set(settledAtom, entries);
}

/** Archive (GET /markets/past/recent) — replaces all archive entries */
export function setArchive(store: Store, entries: SettledEntry[]): void {
  store.set(archiveAtom, entries);
}

/** Bootstrap-time chart hydration (GET /markets/:index/chart): fills a
 * market's history once so sparklines render before any live delta arrives.
 * Never clobbers — once deltas roll, the rolling window is authoritative. */
export function hydrateHistory(
  store: Store,
  index: number,
  points: PricePoint[],
): void {
  const existing = store.get(marketAtom(index));
  if (!existing || existing.history.length > 0) return;
  store.set(marketAtom(index), {
    ...existing,
    history: points.slice(-120),
  });
}

/** market:updated delta — ticks the market atom, keeps history rolling */
export function applyMarketUpdate(
  store: Store,
  rawMarket: Parameters<typeof parseMarket>[0],
): void {
  const index = rawMarket?.index;
  if (typeof index !== 'number') return;
  const existing = store.get(marketAtom(index));
  const snap = parseMarket(rawMarket, existing?.eventId ?? '');
  snap.history = appendHistory(
    existing?.history ?? [],
    snap.price,
    snap.priceTimestamp,
  );
  if (
    existing?.price !== snap.price &&
    snap.price !== null &&
    existing?.price !== null &&
    existing?.price !== undefined
  ) {
    snap.priceDirection = snap.price > existing.price ? 'up' : 'down';
  } else if (snap.price === null || snap.price === existing?.price) {
    snap.priceDirection = existing?.priceDirection ?? 'up';
  }
  store.set(marketAtom(index), snap);
  if (!store.get(marketIdsAtom).includes(index)) {
    store.set(marketIdsAtom, [...store.get(marketIdsAtom), index]);
  }
}

/** event:removed — drops event + markets; returns the removed indexes */
export function applyEventRemoved(
  store: Store,
  eventId: string,
  marketIndexes: number[],
): number[] {
  const events = new Map(store.get(eventsAtom));
  events.delete(eventId);
  store.set(eventsAtom, events);
  store.set(
    marketIdsAtom,
    store.get(marketIdsAtom).filter((id) => !marketIndexes.includes(id)),
  );
  return marketIndexes;
}

// ── Derived (for the screen) ───────────────────────────────────────────

/** Group market/event pairs under their event, keeping first-appearance order. */
function groupByEvent(
  entries: { market: MarketSnapshot; event: EventSnapshot }[],
): EventGroup[] {
  const groups = new Map<string, EventGroup>();
  for (const { market, event } of entries) {
    const group = groups.get(event.eventId);
    if (group) group.markets.push(market);
    else groups.set(event.eventId, { event, markets: [market] });
  }
  return [...groups.values()];
}

/** Archive entries grouped by event (newest-first payload keeps order). */
export function groupArchiveByEvent(entries: SettledEntry[]): EventGroup[] {
  return groupByEvent(entries);
}

export const liveMarketsAtom = atom((get) =>
  get(marketIdsAtom)
    .map((id) => get(marketAtom(id)))
    .filter((m): m is MarketSnapshot => !!m && m.status === 'in_play'),
);

/** Live board grouped: one entry per in-play event, its markets inside. */
export const liveEventGroupsAtom = atom((get) => {
  const events = get(eventsAtom);
  return groupByEvent(
    get(liveMarketsAtom).flatMap((market) => {
      const event = events.get(market.eventId);
      return event ? [{ market, event }] : [];
    }),
  );
});

/** The whole active board (in_play + upcoming + suspended events) grouped,
 * keyed off EVENT status so an in-play event with all markets suspended
 * stays visible. LIVE first, then the rest by kickoff (unknown last). */
export const boardEventGroupsAtom = atom((get) => {
  const events = get(eventsAtom);
  const entries = get(marketIdsAtom)
    .map((id) => get(marketAtom(id)))
    .filter((m): m is MarketSnapshot => !!m)
    .flatMap((market) => {
      const event = events.get(market.eventId);
      return event && event.status !== 'closed' && event.status !== 'settled'
        ? [{ market, event }]
        : [];
    });
  return groupByEvent(entries).sort((a, b) => {
    const rank = (g: EventGroup) =>
      g.event.status === 'in_play' ? 0 : g.event.status === 'suspended' ? 1 : 2;
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    return (
      (a.event.startTime || Number.MAX_SAFE_INTEGER) -
      (b.event.startTime || Number.MAX_SAFE_INTEGER)
    );
  });
});

export const upcomingMarketsAtom = atom((get) => {
  const events = get(eventsAtom);
  return get(marketIdsAtom)
    .map((id) => get(marketAtom(id)))
    .filter((m): m is MarketSnapshot => !!m && m.status === 'upcoming')
    .flatMap((market) => {
      const event = events.get(market.eventId);
      return event ? [{ market, event } satisfies UpcomingEntry] : [];
    })
    .sort((a, b) => a.event.startTime - b.event.startTime);
});

/** Upcoming board grouped by event, earliest kick-off first. */
export const upcomingEventGroupsAtom = atom((get) =>
  groupByEvent(get(upcomingMarketsAtom)),
);
